/**
 * Offline-first engine for Julia AI.
 *
 * - Failed (offline) POST/PUT/DELETE requests are queued in IndexedDB and the
 *   service-worker data cache is patched so the UI immediately reflects the
 *   change ("read your writes" offline).
 * - Photos/documents chosen offline are stored as blobs on the device and a
 *   temporary path is served from the files cache until they upload.
 * - syncOutbox() drains the queue when connectivity returns, remapping
 *   temporary ids and detecting conflicts via updated_at.
 */
import axios from "axios";
import { odb } from "@/lib/offlineDb";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DATA_CACHE = "api-data-v1";
const FILES_CACHE = "api-files-v1";

// Raw client used by the sync engine itself — never queues, so a network
// failure here simply aborts the sync run.
const rawApi = axios.create({ baseURL: API, withCredentials: true });

// Entities that support offline create/edit/delete (all generic CRUD entities).
export const OFFLINE_ENTITIES = new Set([
  "buyers", "sellers", "properties", "viewings", "offers", "leads", "tasks",
  "diary_entries", "templates", "form_templates", "rooms", "parcels",
  "estimation_dossiers", "form_submissions",
]);

export const offlineEvents = new EventTarget();
const emit = (type, detail) => offlineEvents.dispatchEvent(new CustomEvent(type, { detail }));

export const isNetworkError = (error) => !!error && !error.response;

const uuid = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).replace(/-/g, "").slice(0, 20);

const nowIso = () => new Date().toISOString();

/* ------------------------------------------------------------------ */
/* URL parsing                                                          */
/* ------------------------------------------------------------------ */
function parseEntityUrl(url) {
  // url is relative to the api baseURL, e.g. "/buyers" or "/buyers/abc123"
  const clean = url.split("?")[0].replace(/^\//, "");
  const [entity, id] = clean.split("/");
  return { entity, id: id || null };
}

/* ------------------------------------------------------------------ */
/* Cache patching (read-your-writes while offline)                      */
/* ------------------------------------------------------------------ */
async function patchEntityCaches(entity, mapListFn, mapDetail) {
  if (!("caches" in window)) return;
  try {
    const cache = await caches.open(DATA_CACHE);
    const keys = await cache.keys();
    for (const req of keys) {
      const { pathname } = new URL(req.url);
      const isList = pathname === `/api/${entity}`;
      const isDetail = mapDetail && pathname === `/api/${entity}/${mapDetail.id}`;
      if (!isList && !isDetail) continue;
      const res = await cache.match(req);
      if (!res) continue;
      try {
        const data = await res.clone().json();
        const next = isList && Array.isArray(data) ? mapListFn(data) : isDetail ? mapDetail.fn(data) : data;
        await cache.put(req, new Response(JSON.stringify(next), { headers: { "Content-Type": "application/json" } }));
      } catch {
        /* non-JSON cache entry — skip */
      }
    }
  } catch (err) {
    console.warn("Offline cache patch failed", err);
  }
}

/** Last-known copy of a record from the service-worker data cache. */
async function cachedRecord(entity, id) {
  if (!("caches" in window)) return null;
  try {
    const cache = await caches.open(DATA_CACHE);
    const detail = await cache.match(`${API}/${entity}/${id}`);
    if (detail) return await detail.clone().json();
    const listRes = await cache.match(`${API}/${entity}`);
    if (listRes) {
      const list = await listRes.clone().json();
      if (Array.isArray(list)) return list.find((r) => r.id === id) || null;
    }
  } catch (err) {
    console.warn("Offline cache read failed", err);
  }
  return null;
}

/** Move a card between columns in every cached /api/pipeline board. */
async function patchPipelineCache(propertyId, stage) {
  if (!("caches" in window) || !stage) return;
  try {
    const cache = await caches.open(DATA_CACHE);
    for (const req of await cache.keys()) {
      const { pathname } = new URL(req.url);
      if (pathname !== "/api/pipeline") continue;
      const res = await cache.match(req);
      if (!res) continue;
      try {
        const data = await res.clone().json();
        if (!data?.columns) continue;
        let card = null;
        for (const key of Object.keys(data.columns)) {
          const idx = data.columns[key].findIndex((c) => c.id === propertyId);
          if (idx >= 0) {
            card = data.columns[key][idx];
            data.columns[key].splice(idx, 1);
            break;
          }
        }
        if (card) {
          card.pipeline_stage = stage;
          (data.columns[stage] = data.columns[stage] || []).unshift(card);
          await cache.put(req, new Response(JSON.stringify(data), { headers: { "Content-Type": "application/json" } }));
        }
      } catch {
        /* non-JSON entry */
      }
    }
  } catch (err) {
    console.warn("Pipeline cache patch failed", err);
  }
}

/* ------------------------------------------------------------------ */
/* Mutation queueing                                                    */
/* ------------------------------------------------------------------ */
const withBase = (r, patch) => ({ ...r, ...patch, _base_updated_at: r._base_updated_at || r.updated_at });

export async function queueMutation(config) {
  const method = (config.method || "get").toLowerCase();
  const url = config.url || "";

  let body = null;
  if (config.data) {
    body = typeof config.data === "string" ? JSON.parse(config.data) : config.data;
  }

  // Pipeline stage moves (kanban drag) — queue and move the card in the cached board
  const stageMatch = url.match(/^\/pipeline\/([^/]+)\/stage$/);
  if (method === "put" && stageMatch) {
    await odb.outbox.add({
      ts: Date.now(), method, url, entity: "pipeline", recordId: stageMatch[1],
      tempId: null, baseUpdatedAt: "", body: JSON.stringify(body || {}),
    });
    await patchPipelineCache(stageMatch[1], body?.stage);
    await patchEntityCaches(
      "properties",
      (list) => list.map((r) => (r.id === stageMatch[1] ? { ...r, pipeline_stage: body?.stage } : r)),
      { id: stageMatch[1], fn: (r) => ({ ...r, pipeline_stage: body?.stage }) }
    );
    emit("queued", { entity: "pipeline", method });
    return { ok: true, stage: body?.stage, _offline: true };
  }

  const { entity, id } = parseEntityUrl(url);
  if (!OFFLINE_ENTITIES.has(entity)) return null;

  const item = {
    ts: Date.now(),
    method,
    url,
    entity,
    recordId: id,
    tempId: null,
    baseUpdatedAt: (body && body.updated_at) || "",
    body: body ? JSON.stringify(body) : null,
  };

  let synthetic;
  if (method === "post") {
    const tempId = `off${uuid()}`;
    item.tempId = tempId;
    const record = { ...body, id: tempId, created_at: nowIso(), updated_at: nowIso(), _offline: true };
    synthetic = record;
    await odb.outbox.add(item);
    // rooms/parcels display in insertion order — append; everything else newest-first
    const append = entity === "rooms" || entity === "parcels";
    await patchEntityCaches(entity, (list) => (append ? [...list, record] : [record, ...list]));
  } else if (method === "put") {
    const all = await odb.outbox.toArray();
    const patch = { ...body, updated_at: nowIso(), _offline: true };
    synthetic = { ...patch, id };

    const queuedPost = all.find((it) => it.method === "post" && it.tempId === id);
    if (queuedPost) {
      // Editing a record that was itself created offline → merge into its queued POST
      const merged = { ...(queuedPost.body ? JSON.parse(queuedPost.body) : {}), ...body };
      delete merged.id;
      delete merged._offline;
      delete merged.updated_at;
      await odb.outbox.update(queuedPost.id, { body: JSON.stringify(merged) });
    } else {
      const queuedPut = all.find((it) => it.method === "put" && it.url === url);
      if (queuedPut) {
        // Second offline edit of the same record → merge, keep the original conflict baseline
        const merged = { ...(queuedPut.body ? JSON.parse(queuedPut.body) : {}), ...body };
        await odb.outbox.update(queuedPut.id, { body: JSON.stringify(merged) });
      } else {
        if (!item.baseUpdatedAt) {
          const cached = await cachedRecord(entity, id);
          item.baseUpdatedAt = cached?._base_updated_at || cached?.updated_at || "";
        }
        await odb.outbox.add(item);
      }
    }
    await patchEntityCaches(entity, (list) => list.map((r) => (r.id === id ? withBase(r, patch) : r)), {
      id, fn: (r) => withBase(r, patch),
    });
  } else if (method === "delete") {
    synthetic = { ok: true };
    const all = await odb.outbox.toArray();
    // Drop any queued edits for this record
    for (const it of all) {
      if (it.method === "put" && it.entity === entity && it.recordId === id) await odb.outbox.delete(it.id);
    }
    const queuedPost = all.find((it) => it.method === "post" && it.tempId === id);
    if (queuedPost) {
      await odb.outbox.delete(queuedPost.id); // never reached the server — nothing to delete
    } else {
      await odb.outbox.add(item);
    }
    await patchEntityCaches(entity, (list) => list.filter((r) => r.id !== id));
  } else {
    return null;
  }

  emit("queued", { entity, method });
  return synthetic;
}

/* ------------------------------------------------------------------ */
/* Offline photo/document capture                                       */
/* ------------------------------------------------------------------ */
export async function queueOfflineUpload(file) {
  const ext = (file.name || "photo.jpg").split(".").pop().toLowerCase() || "jpg";
  const tempPath = `offline/${uuid()}.${ext}`;
  await odb.blobs.put({ tempPath, blob: file, name: file.name || tempPath, type: file.type || "application/octet-stream" });
  // Serve the local blob at its future URL so <img src=".../files/offline/..."> works immediately.
  try {
    const cache = await caches.open(FILES_CACHE);
    await cache.put(
      new Request(`${API}/files/${tempPath}`),
      new Response(file, { headers: { "Content-Type": file.type || "application/octet-stream" } })
    );
  } catch (err) {
    console.warn("Could not cache offline upload preview", err);
  }
  emit("queued", { entity: "upload", method: "post" });
  return { path: tempPath, original_filename: file.name || tempPath, content_type: file.type || "" };
}

const TEMP_PATH_RE = /offline\/[a-z0-9]+\.[a-z0-9]+/gi;
const TEMP_ID_RE = /off[a-z0-9]{16,24}/gi;

async function uploadPendingBlobs(bodyStr) {
  // Upload any device-stored blobs referenced in this mutation; return replacements.
  const replacements = {};
  const paths = [...new Set(bodyStr.match(TEMP_PATH_RE) || [])];
  for (const tempPath of paths) {
    const rec = await odb.blobs.get(tempPath);
    if (!rec) continue; // already uploaded in an earlier run
    const fd = new FormData();
    fd.append("file", new File([rec.blob], rec.name, { type: rec.type }));
    const { data } = await rawApi.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    replacements[tempPath] = data.path;
    await odb.blobs.delete(tempPath);
    // Move the cached preview to the real URL, drop the temp entry.
    try {
      const cache = await caches.open(FILES_CACHE);
      const tempReq = new Request(`${API}/files/${tempPath}`);
      const hit = await cache.match(tempReq);
      if (hit) {
        await cache.put(new Request(`${API}/files/${data.path}`), hit.clone());
        await cache.delete(tempReq);
      }
    } catch { /* preview move is best-effort */ }
  }
  return replacements;
}

const applyReplacements = (str, map) =>
  Object.entries(map).reduce((s, [from, to]) => s.split(from).join(to), str);

async function rewriteOutbox(map) {
  if (!Object.keys(map).length) return;
  const items = await odb.outbox.toArray();
  for (const it of items) {
    const nextUrl = applyReplacements(it.url, map);
    const nextBody = it.body ? applyReplacements(it.body, map) : it.body;
    if (nextUrl !== it.url || nextBody !== it.body) {
      await odb.outbox.update(it.id, { url: nextUrl, body: nextBody });
    }
  }
}

/* ------------------------------------------------------------------ */
/* Sync                                                                 */
/* ------------------------------------------------------------------ */
export const pendingCount = async () => (await odb.outbox.count()) + (await odb.blobs.count());

/**
 * Drain the outbox. Returns { synced, conflicts, failed, offline }.
 * Conflicted items stay in the outbox until resolved via resolveConflict().
 */
export async function syncOutbox() {
  const result = { synced: 0, conflicts: [], failed: [], offline: false };
  const items = await odb.outbox.orderBy("id").toArray();

  for (const item of items) {
    // Re-read: earlier iterations may have rewritten this item (id remaps)
    const fresh = await odb.outbox.get(item.id);
    if (!fresh) continue;
    let url = fresh.url;
    let bodyStr = fresh.body;

    try {
      if (bodyStr && TEMP_PATH_RE.test(bodyStr)) {
        TEMP_PATH_RE.lastIndex = 0;
        const blobMap = await uploadPendingBlobs(bodyStr);
        bodyStr = applyReplacements(bodyStr, blobMap);
        await odb.outbox.update(fresh.id, { body: bodyStr });
        await rewriteOutbox(blobMap);
      }

      // Skip mutations still referencing an unresolved temp id (their POST failed)
      TEMP_ID_RE.lastIndex = 0;
      if (TEMP_ID_RE.test(url)) {
        result.failed.push({ item: fresh, reason: "unresolved-reference" });
        continue;
      }

      const body = bodyStr ? JSON.parse(bodyStr) : undefined;

      if (fresh.method === "put" && fresh.baseUpdatedAt) {
        // Conflict check: has the server copy changed since we loaded it?
        let server = null;
        try {
          server = (await rawApi.get(url)).data;
        } catch (err) {
          if (isNetworkError(err)) throw err;
          // 404 → record deleted on server; treat as conflict with empty server copy
        }
        if (server && server.updated_at && server.updated_at !== fresh.baseUpdatedAt) {
          result.conflicts.push({ outboxId: fresh.id, entity: fresh.entity, url, mine: body, server });
          continue;
        }
      }

      let res;
      if (fresh.method === "post") res = await rawApi.post(url, body);
      else if (fresh.method === "put") res = await rawApi.put(url, body);
      else if (fresh.method === "delete") res = await rawApi.delete(url);

      if (fresh.method === "post" && fresh.tempId && res?.data?.id) {
        await rewriteOutbox({ [fresh.tempId]: res.data.id });
      }
      await odb.outbox.delete(fresh.id);
      result.synced += 1;
    } catch (err) {
      if (isNetworkError(err)) {
        result.offline = true;
        break; // still offline — stop, retry later
      }
      if (err.response?.status === 404 && fresh.method === "delete") {
        await odb.outbox.delete(fresh.id); // already gone server-side
        result.synced += 1;
        continue;
      }
      result.failed.push({ item: fresh, reason: err.response?.data?.detail || err.message });
      await odb.outbox.delete(fresh.id); // don't wedge the queue on permanent errors
    }
  }

  emit("synced", result);
  return result;
}

/** Resolve a conflict: keep = "mine" (force my version) or "server" (discard mine). */
export async function resolveConflict(conflict, keep) {
  const { id } = parseEntityUrl(conflict.url);
  if (keep === "mine") {
    const body = { ...conflict.mine };
    delete body.updated_at;
    delete body._offline;
    delete body._base_updated_at;
    if (conflict.server) {
      await rawApi.put(conflict.url, body);
    } else {
      // Record was deleted on the server — keeping mine recreates it
      delete body.id;
      await rawApi.post(`/${conflict.entity}`, body);
    }
  } else if (conflict.server) {
    // Put the server version back into local caches
    await patchEntityCaches(
      conflict.entity,
      (list) => list.map((r) => (r.id === conflict.server?.id ? conflict.server : r)),
      { id: conflict.server.id, fn: () => conflict.server }
    );
  } else {
    // Server deleted it and the user agreed — drop it locally too
    await patchEntityCaches(conflict.entity, (list) => list.filter((r) => r.id !== id));
  }
  await odb.outbox.delete(conflict.outboxId);
  emit("queued", {});
}

/* ------------------------------------------------------------------ */
/* Offline reads: serve filtered/searched lists from the cached copy    */
/* ------------------------------------------------------------------ */
async function offlineGetFallback(config) {
  if (!("caches" in window)) return null;
  const rawUrl = config.url || "";
  const [pathPart, queryPart] = rawUrl.split("?");
  const params = { ...(config.params || {}) };
  if (queryPart) for (const [k, v] of new URLSearchParams(queryPart)) params[k] = v;
  const cache = await caches.open(DATA_CACHE);

  // Filtered pipeline board → fall back to the cached unfiltered board
  if (pathPart.replace(/\/$/, "") === "/pipeline") {
    const hit = await cache.match(`${API}/pipeline`);
    return hit ? await hit.clone().json() : null;
  }
  // Offers for one property → filter the cached global offers list
  const offersMatch = pathPart.match(/^\/pipeline\/([^/]+)\/offers$/);
  if (offersMatch) {
    const hit = await cache.match(`${API}/offers`);
    if (!hit) return null;
    const list = await hit.clone().json();
    return Array.isArray(list) ? list.filter((o) => o.property_id === offersMatch[1]) : null;
  }

  const { entity, id } = parseEntityUrl(pathPart);
  if (!OFFLINE_ENTITIES.has(entity)) return null;
  const hit = await cache.match(`${API}/${entity}`);
  if (!hit) return null;
  const list = await hit.clone().json();
  if (!Array.isArray(list)) return null;
  if (id) return list.find((r) => r.id === id) || null;

  let out = list;
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === "") continue;
    if (k === "search") {
      const q = String(v).toLowerCase();
      out = out.filter((r) =>
        Object.values(r).some((val) => typeof val === "string" && val.toLowerCase().includes(q))
      );
    } else {
      out = out.filter((r) => String(r[k] ?? "") === String(v));
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Full offline copy: pre-download every list, board and file           */
/* ------------------------------------------------------------------ */
let lastWarmAt = 0;

export async function warmOfflineCache(force = false) {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  if (!force && Date.now() - lastWarmAt < 5 * 60 * 1000) return;
  try {
    // Never clobber unsynced local edits with server copies
    if ((await odb.outbox.count()) > 0) return;
  } catch {
    return;
  }
  lastWarmAt = Date.now();

  // Warm the app shell + static assets so a cold first visit still opens offline
  // (the JS bundle is normally fetched before the service worker takes control).
  try {
    const shellRes = await fetch("/", { credentials: "include" });
    const html = await shellRes.text();
    const assets = [...html.matchAll(/(?:src|href)="(\/[^"]+\.(?:js|css|png|json|ico))"/g)].map((m) => m[1]);
    await Promise.allSettled(assets.map((a) => fetch(a)));
  } catch {
    /* shell warm is best-effort */
  }

  const d = new Date();
  const months = [0, 1].map((off) => {
    const m = new Date(d.getFullYear(), d.getMonth() + off, 1);
    return `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`;
  });
  const urls = [
    ...[...OFFLINE_ENTITIES].map((e) => `/${e}`),
    "/dashboard", "/pipeline", "/pipeline/stages", "/placeholders",
    ...months.map((m) => `/calendar?month=${m}`),
  ];

  // Fetching through the service worker populates the data cache
  const bodies = [];
  await Promise.allSettled(
    urls.map(async (u) => {
      const res = await fetch(`${API}${u}`, { credentials: "include" });
      if (res.ok) bodies.push(await res.text());
    })
  );

  // Pre-cache every referenced photo/document (files cache is cache-first,
  // so anything already downloaded is skipped instantly).
  const paths = new Set();
  const fileRe = /[\w.-]+\/uploads\/[0-9a-fA-F-]+\.[A-Za-z0-9]+/g;
  for (const b of bodies) for (const m of b.match(fileRe) || []) paths.add(m);
  const queue = [...paths];
  await Promise.all(
    Array.from({ length: 4 }, async () => {
      while (queue.length) {
        const p = queue.pop();
        try {
          await fetch(`${API}/files/${p}`, { credentials: "include" });
        } catch {
          /* file will be retried on the next warm-up */
        }
      }
    })
  );
  emit("warmed", { files: paths.size });
}

/* ------------------------------------------------------------------ */
/* Axios interceptor                                                    */
/* ------------------------------------------------------------------ */
export function installOfflineInterceptor(api) {
  api.interceptors.response.use(
    (res) => {
      emit("network-ok", {});
      return res;
    },
    async (error) => {
      const config = error.config || {};
      if (!isNetworkError(error) || config._noOfflineQueue) throw error;
      const method = (config.method || "get").toLowerCase();
      emit("network-down", {});

      if (method === "get") {
        // Cache miss in the service worker (e.g. filtered/search URL) —
        // answer from the cached full list, filtered client-side.
        const fallback = await offlineGetFallback(config).catch(() => null);
        if (fallback !== null && fallback !== undefined) {
          return { data: fallback, status: 200, statusText: "OK (offline cache)", headers: {}, config };
        }
        throw error;
      }

      // Offline photo/document capture
      if (config.url === "/upload" && config.data instanceof FormData) {
        const file = config.data.get("file");
        if (file) {
          const data = await queueOfflineUpload(file);
          return { data, status: 200, statusText: "OK (offline)", headers: {}, config };
        }
      }

      const synthetic = await queueMutation(config);
      if (synthetic) {
        return { data: synthetic, status: 200, statusText: "OK (offline)", headers: {}, config };
      }
      throw error;
    }
  );
}
